import {
  CameraOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FireOutlined,
  GlobalOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  List,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Timeline,
  Typography,
  Upload,
} from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import * as Component from 'components';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import { LanguageInterface } from 'content/global-settings/types/globalSettingType';
import { default as React, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useParams } from 'react-router-dom';
import { videoService } from 'service/api/videoService';
import { actionHistoryService } from 'src/domain/account/action-history/service/actionHistoryService';
import { ActionHistoryType } from 'src/domain/account/action-history/types/actionHistoryType';
import { videoSubtitleService } from 'src/domain/video/assets/services/videoSubtitleService';
import { LANGUAGE_OPTIONS, VideoSubtitleType } from 'type/videoSubtitleType';
import { getPlayableVideoUrl, hasPlayableVideoUrl, VideoType } from 'type/videoType';
import BurnInStatusTag from '../../../../domain/subtitle-burnin/components/BurnInStatusTag';
import { burnInService } from '../../../../domain/subtitle-burnin/services/burnInService';
import { BurnInJob } from '../../../../domain/subtitle-burnin/types/burnInTypes';
import { VideoThumbnailType } from '../../../../shared/type/videoThumbnailType';
import { formatDuration } from '../../../../shared/utils/timeFormat';
import Preview from '../components/preview';

const { Title, Text } = Typography;

interface SubtitleFormData {
  lang: string;
  uploadMethod: 'file' | 'url';
  file?: UploadFile[];
  external_url?: string;
  is_integrated: boolean;
}

const AssetsEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transcoding, setTranscoding] = useState(false);
  const [ableTranscode, setAbleTranscode] = useState(false);
  const [generatingSample, setGeneratingSample] = useState(false);
  const [verifyingVideo, setVerifyingVideo] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [video, setVideo] = useState<VideoType | null>(null);
  const [subtitles, setSubtitles] = useState<VideoSubtitleType[]>([]);
  const [subtitleLanguages, setSubtitleLanguages] = useState<LanguageInterface[]>([]);
  const [userActionHistories, setUserActionHistories] = useState<ActionHistoryType[]>([]);

  const [subtitleModalVisible, setSubtitleModalVisible] = useState(false);
  const [editingSubtitle, setEditingSubtitle] = useState<VideoSubtitleType | null>(null);

  const [burnInJobs, setBurnInJobs] = useState<BurnInJob[]>([]);
  const [burnInModalVisible, setBurnInModalVisible] = useState(false);
  const [burnInLoading, setBurnInLoading] = useState(false);
  const [selectedBurnInLangs, setSelectedBurnInLangs] = useState<number[]>([]);

  const [subtitleForm] = Form.useForm<SubtitleFormData>();
  const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');

  // Thumbnail screenshot state
  const [thumbnailImage, setThumbnailImage] = useState<string | null>(null);
  const [defaultThumbnail, setDefaultThumbnail] = useState<VideoThumbnailType | null>(null);
  const [languageThumbnails, setLanguageThumbnails] = useState<Map<string, VideoThumbnailType>>(
    new Map()
  );
  const [uploadedThumbnails, setUploadedThumbnails] = useState<Map<string, string>>(new Map());
  const playerRef = useRef<ReactPlayer>(null);

  // Thumbnail language options (matching the image)
  const THUMBNAIL_LANGUAGES = [
    { value: 'en', label: 'English', flag: '🇺🇸' },
    { value: 'ko', label: 'Korean', flag: '🇰🇷' },
    { value: 'zh', label: 'Chinese', flag: '🇨🇳' },
    { value: 'vi', label: 'Vietnamese', flag: '🇻🇳' },
  ];

  // Thumbnail Distribution Rules (matching the image)
  const THUMBNAIL_DISTRIBUTION_RULES = [
    { language: 'English', service: 'AmasianTV' },
    { language: 'Korean', service: 'ODKR' },
    { language: 'Chinese', service: 'ODC' },
  ];

  const fetchData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [videoData, subtitleData, subtitleLanguagesData, actionHistoryData] = await Promise.all(
        [
          videoService.fetchById(Number(id)),
          videoSubtitleService.fetchByVideoId(Number(id)),
          globalSettingService.fetchLanguages(
            new URLSearchParams({ type: 'subtitle', page_size: '1000' })
          ),
          actionHistoryService.fetchByParent('video', Number(id)),
        ]
      );

      setVideo(videoData);
      setSubtitles(subtitleData);
      setSubtitleLanguages(subtitleLanguagesData.results);
      setUserActionHistories(actionHistoryData.data);

      try {
        const burnInData = await burnInService.fetchByVideo(Number(id));
        setBurnInJobs(burnInData);
      } catch (burnInError) {
        console.error('Burn-in data loading error:', burnInError);
      }

      // Process thumbnails: separate default and language-specific
      if (videoData.video_thumbnails && videoData.video_thumbnails.length > 0) {
        const defaultThumb = videoData.video_thumbnails.find(t => t.is_default === true);
        if (defaultThumb) {
          setDefaultThumbnail(defaultThumb);
          setThumbnailImage(defaultThumb.url);
        }

        // Group language-specific thumbnails
        const langMap = new Map<string, VideoThumbnailType>();
        videoData.video_thumbnails
          .filter(t => t.language && !t.is_default)
          .forEach(t => {
            if (t.language) {
              langMap.set(t.language.toLowerCase(), t);
            }
          });
        setLanguageThumbnails(langMap);
      }

      if (videoData?.status?.toLowerCase() === 'completed') {
        setAbleTranscode(true);
      }
    } catch (error) {
      console.error('Data loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openSubtitleModal = (subtitle?: VideoSubtitleType) => {
    setEditingSubtitle(subtitle || null);
    if (subtitle) {
      subtitleForm.setFieldsValue({
        lang: subtitle.lang,
        uploadMethod: subtitle.file ? 'file' : 'url',
        external_url: subtitle.external_url,
        is_integrated: subtitle.is_integrated,
      });
      setUploadMethod(subtitle.file ? 'file' : 'url');
    } else {
      subtitleForm.resetFields();
      subtitleForm.setFieldsValue({
        uploadMethod: 'file',
        is_integrated: false,
      });
      setUploadMethod('file');
    }
    setSubtitleModalVisible(true);
  };

  const handleSaveSubtitle = async (values: SubtitleFormData) => {
    if (!id) return;

    setSaving(true);
    console.log(values);
    try {
      if (values.file && values.file.length > 0) {
        await videoSubtitleService.uploadSubtitleFile(
          Number(id),
          values.file[0].originFileObj!,
          values.lang,
          values.is_integrated
        );
      } else {
        throw new Error('Please enter a file');
      }

      message.success('Subtitle has been saved.');
      setSubtitleModalVisible(false);
      await fetchData();
    } catch (error) {
      console.error('Subtitle save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubtitle = async (subtitleId: number) => {
    try {
      await videoSubtitleService.delete(subtitleId);
      message.success('Subtitle has been deleted.');
      await fetchData();
    } catch (error) {
      console.error('Subtitle delete error:', error);
    }
  };

  const handleTranscode = async () => {
    if (!id) return;

    setTranscoding(true);
    try {
      await videoService.videoAssetTranscode(Number(id));
      message.success('Video transcoding has been started.');
      window.location.reload();
    } catch (error) {
      console.error('Transcode error:', error);
    } finally {
      setTranscoding(false);
    }
  };

  const handleRecover = async () => {
    if (!id) return;

    const isRecovering = video?.status?.toLowerCase() === 'recovering';
    if (isRecovering) {
      message.warning('Recovery is already in progress.');
      return;
    }

    setRecovering(true);
    try {
      await videoService.recoverVideo(Number(id));
      message.success('Recovery has been requested. Please refresh after 5-10 minutes to check.');
      await fetchData();
    } catch (error: any) {
      const reason = error?.response?.data?.reason;
      if (reason === 'original_ok') {
        message.info('The original file has no issues.');
      } else {
        console.error('Recovery error:', error);
      }
    } finally {
      setRecovering(false);
    }
  };

  const handleGenerateSampleVideo = async () => {
    if (!id) return;

    setGeneratingSample(true);
    try {
      await videoService.submitSampleVideo(Number(id));
      message.success('Sample video generation has been started.');
      await fetchData();
    } catch (error) {
      console.error('Generate sample video error:', error);
    } finally {
      setGeneratingSample(false);
    }
  };

  const handleVerifyVideoFiles = async () => {
    if (!id) return;

    setVerifyingVideo(true);
    try {
      await videoService.verifyVideoFiles(Number(id));
      message.success('Video file verification batch job has been submitted.');
      await fetchData();
    } catch (error) {
      console.error('Verify video files error:', error);
    } finally {
      setVerifyingVideo(false);
    }
  };

  const handleBurnIn = async () => {
    setBurnInLoading(true);
    try {
      for (const subtitleId of selectedBurnInLangs) {
        const contentId = video?.contents?.[0]?.id;
        if (contentId) {
          await burnInService.createSingleJob(contentId, subtitleId);
        }
      }
      message.success('Burn-in jobs created successfully');
      setBurnInModalVisible(false);
      setSelectedBurnInLangs([]);
      // Refresh burn-in jobs
      const updatedJobs = await burnInService.fetchByVideo(Number(id));
      setBurnInJobs(updatedJobs);
    } catch (error) {
      message.error('Failed to create burn-in jobs');
    } finally {
      setBurnInLoading(false);
    }
  };

  const getLanguageInfo = (langCode: string) => {
    // First search in subtitleLanguages
    const subtitleLang = subtitleLanguages.find(
      lang => lang.rfc_code.toLowerCase() === langCode.toLowerCase()
    );
    if (subtitleLang) {
      return {
        value: subtitleLang.rfc_code.toLowerCase(),
        label: subtitleLang.name,
        flag: '🌐',
      };
    }
    // If not found, search in existing LANGUAGE_OPTIONS
    return (
      LANGUAGE_OPTIONS.find(lang => lang.value === langCode) || {
        value: langCode,
        label: langCode,
        flag: '🌐',
      }
    );
  };

  const getStatusDisplay = (status: string) => {
    const statusValue = status.toLowerCase();
    switch (statusValue) {
      case 'completed':
        return { color: 'green', label: 'Completed' };
      case 'failed':
        return { color: 'red', label: 'Failed' };
      case 'in_progress':
      case 'processing':
        return { color: 'orange', label: 'In Progress' };
      case 'transcoding':
        return { color: 'orange', label: 'Transcoding' };
      case 'creating':
        return { color: 'blue', label: 'Creating' };
      case 'start':
        return { color: 'default', label: 'Start' };
      case 'uploading':
        return { color: 'blue', label: 'Uploading' };
      case 'recovering':
        return { color: 'orange', label: 'Recovering' };
      case 'empty':
        return { color: 'default', label: 'Waiting' };
      default:
        return { color: 'default', label: 'Unknown' };
    }
  };

  const handleTakeScreenshot = () => {
    if (!playerRef.current) {
      Modal.error({ title: 'Error', content: 'Video player is not ready', okText: 'Confirm' });
      return;
    }

    try {
      const internalPlayer = playerRef.current.getInternalPlayer();
      const videoEl = internalPlayer as HTMLVideoElement;

      if (!videoEl || videoEl.readyState < 2) {
        Modal.error({ title: 'Error', content: 'Video is not ready. Please wait for the video to load.', okText: 'Confirm' });
        return;
      }

      // Ensure crossOrigin is set (in case it wasn't set during load)
      if (!videoEl.crossOrigin) {
        videoEl.crossOrigin = 'anonymous';
        // If crossOrigin wasn't set, we need to reload the video
        message.warning('Setting crossOrigin attribute. Please try again in a moment.');
        return;
      }

      // Target resolution: 1280x720 (16:9)
      const targetWidth = 1280;
      const targetHeight = 720;
      const targetAspectRatio = targetWidth / targetHeight;

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        Modal.error({ title: 'Error', content: 'Failed to create canvas context', okText: 'Confirm' });
        return;
      }

      // Fill canvas with black background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      // Calculate video aspect ratio
      const videoWidth = videoEl.videoWidth;
      const videoHeight = videoEl.videoHeight;
      const videoAspectRatio = videoWidth / videoHeight;

      let drawWidth, drawHeight, offsetX, offsetY;

      if (videoAspectRatio > targetAspectRatio) {
        // Video is wider than target - add letterbox (black bars on top/bottom)
        drawWidth = targetWidth;
        drawHeight = targetWidth / videoAspectRatio;
        offsetX = 0;
        offsetY = (targetHeight - drawHeight) / 2;
      } else {
        // Video is taller than target - add pillarbox (black bars on left/right)
        drawHeight = targetHeight;
        drawWidth = targetHeight * videoAspectRatio;
        offsetX = (targetWidth - drawWidth) / 2;
        offsetY = 0;
      }

      // Draw video frame centered with black bars
      ctx.drawImage(videoEl, offsetX, offsetY, drawWidth, drawHeight);

      // Check if canvas is tainted before calling toDataURL
      try {
        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
        setThumbnailImage(imageDataUrl);
        message.success('Screenshot captured successfully! (1280x720)');
      } catch (dataUrlError: any) {
        if (dataUrlError.name === 'SecurityError' || dataUrlError.message.includes('Tainted')) {
          Modal.error({
            title: 'Error',
            content: 'CORS error: The video server needs to allow cross-origin requests. ' +
              'Please ensure the server sends proper CORS headers.',
            okText: 'Confirm',
          });
          console.error(
            'Canvas tainted. CORS headers may be missing from video server:',
            dataUrlError
          );
        } else {
          throw dataUrlError;
        }
      }
    } catch (error) {
      console.error('Screenshot error:', error);
      Modal.error({ title: 'Error', content: 'Failed to capture screenshot', okText: 'Confirm' });
    }
  };

  const handleThumbnailUpload = (language: string, file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const imageDataUrl = e.target?.result as string;
      setUploadedThumbnails(prev => new Map(prev).set(language, imageDataUrl));
      message.success(
        `${THUMBNAIL_LANGUAGES.find(l => l.value === language)?.label} thumbnail uploaded successfully!`
      );
    };
    reader.onerror = () => {
      Modal.error({ title: 'Error', content: 'Failed to read image file', okText: 'Confirm' });
    };
    reader.readAsDataURL(file);
    return false; // Prevent default upload
  };

  const handleDeleteLanguageThumbnail = async (language: string) => {
    const thumbnail = languageThumbnails.get(language);
    if (!thumbnail) return;

    try {
      await videoService.deleteThumbnail(thumbnail.id);
      message.success(
        `${THUMBNAIL_LANGUAGES.find(l => l.value === language)?.label} thumbnail deleted successfully!`
      );
      await fetchData();
    } catch (error: any) {
      console.error('Delete thumbnail error:', error);
    }
  };

  const handleSaveDefaultThumbnail = async () => {
    if (!id) {
      Modal.error({ title: 'Error', content: 'Video ID is missing', okText: 'Confirm' });
      return;
    }

    if (!thumbnailImage) {
      message.warning('No default thumbnail to save');
      return;
    }

    try {
      setSaving(true);
      const base64Response = await fetch(thumbnailImage);
      const blob = await base64Response.blob();
      if (defaultThumbnail) {
        await videoService.updateThumbnail(defaultThumbnail.id, blob, true, null);
        message.success('Default thumbnail updated successfully!');
      } else {
        await videoService.uploadThumbnail(Number(id), blob, true, null);
        message.success('Default thumbnail saved successfully!');
      }

      // Clear thumbnail image after save
      setThumbnailImage(null);

      // Refresh data
      await fetchData();
    } catch (error: any) {
      console.error('Save default thumbnail error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllLanguageThumbnails = async () => {
    if (!id) {
      Modal.error({ title: 'Error', content: 'Video ID is missing', okText: 'Confirm' });
      return;
    }

    const hasLanguageThumbnailsToSave = Array.from(uploadedThumbnails.values()).some(img => img);

    if (!hasLanguageThumbnailsToSave) {
      message.warning('No language thumbnails to save');
      return;
    }

    try {
      setSaving(true);
      const savePromises: Promise<any>[] = [];

      // Save all language-specific thumbnails
      for (const [language, imageDataUrl] of uploadedThumbnails.entries()) {
        const base64Response = await fetch(imageDataUrl);
        const blob = await base64Response.blob();
        const existingThumbnail = languageThumbnails.get(language);
        if (existingThumbnail) {
          savePromises.push(
            videoService.updateThumbnail(existingThumbnail.id, blob, false, language)
          );
        } else {
          savePromises.push(videoService.uploadThumbnail(Number(id), blob, false, language));
        }
      }

      await Promise.all(savePromises);
      message.success('All language thumbnails saved successfully!');

      // Clear all uploaded language thumbnails after save
      setUploadedThumbnails(new Map());

      // Refresh data
      await fetchData();
    } catch (error: any) {
      console.error('Save language thumbnails error:', error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!video) {
    return <div>Video not found.</div>;
  }

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            <FileTextOutlined style={{ marginRight: '12px' }} />
            Video Asset ( {video.file_name} )
          </Title>
          <Text type="secondary">Manage video information and subtitles</Text>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card title="Video Information" style={{ minHeight: '500px', height: '100%' }}>
              {video.status?.toLowerCase() === 'copying' ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <Spin size="large" />
                  <br />
                  <br />
                  <Text type="secondary">Video is being copied from another source.</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Please wait...
                  </Text>
                </div>
              ) : video.file_url ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>File Name</Text>
                    <br />
                    <Text>{video.file_name}</Text>
                  </div>

                  <div style={{ overflowWrap: 'break-word', wordBreak: 'break-all' }}>
                    <Text strong>File Url</Text>
                    <br />
                    <a href={video.file_url} target="_blank" rel="noreferrer">
                      {video.file_url}
                    </a>
                  </div>

                  {video.mediahold_files?.original_files &&
                    Object.keys(video.mediahold_files.original_files).length > 0 && (
                      <div>
                        <Text strong>Mediahold Files</Text>
                        <br />
                        <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {Object.entries(video.mediahold_files.original_files).map(
                            ([preset, entry]) => {
                              const url = entry.s3_path.startsWith('http')
                                ? entry.s3_path
                                : `https://hls-mh.ondemandkorea.com/${entry.s3_path}`;
                              return (
                                <a
                                  key={preset}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ fontSize: '12px' }}
                                >
                                  {preset}
                                </a>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}

                  <div>
                    <Text strong>Sample Video</Text>
                    <br />
                    {video.sample_videos && video.sample_videos.length > 0 ? (
                      <>
                        {video.sample_videos[0].preset && (
                          <Text
                            type="secondary"
                            style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}
                          >
                            Preset: {video.sample_videos[0].preset}
                          </Text>
                        )}
                        {video.sample_videos[0].status === 'completed' ? (
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => {
                              if (video.sample_videos && video.sample_videos[0]?.file_url) {
                                window.open(video.sample_videos[0].file_url, '_blank');
                              }
                            }}
                          >
                            Download Sample Video
                          </Button>
                        ) : (
                          <Button type="primary" icon={<LoadingOutlined />} disabled={true}>
                            Generating...
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        type="default"
                        icon={<UploadOutlined />}
                        onClick={handleGenerateSampleVideo}
                        loading={generatingSample}
                        disabled={generatingSample}
                      >
                        Generate Sample Video
                      </Button>
                    )}
                  </div>

                  <div>
                    <Text strong>File Size</Text>
                    <br />
                    <Text>
                      {video.file_size
                        ? `${Math.round(video.file_size / (1024 * 1024))} MB`
                        : 'Unknown'}
                    </Text>
                  </div>
                  {video.duration && (
                    <div>
                      <Text strong>Duration</Text>
                      <br />
                      <Text>{formatDuration(video.duration)}</Text>
                    </div>
                  )}

                  <div>
                    <Text strong>Status</Text>
                    <br />
                    <Tag color={getStatusDisplay(video.status || '').color}>
                      {getStatusDisplay(video.status || '').label}
                    </Tag>
                  </div>

                  {video.ingest_source && (
                    <div>
                      <Text strong>Ingest Source</Text>
                      <br />
                      <Tag
                        color={
                          video.ingest_source.toLowerCase() === 'mediahold'
                            ? 'blue'
                            : video.ingest_source.toLowerCase() === 'kcp'
                              ? 'purple'
                              : video.ingest_source.toLowerCase() === 'in-house'
                                ? 'green'
                                : 'default'
                        }
                      >
                        {video.ingest_source.toUpperCase()}
                      </Tag>
                    </div>
                  )}

                  <div>
                    <Text strong>File Preview</Text>
                    <br />
                    {video.file_url ? (
                      <Preview videoUrl={video.file_url} subtitles={subtitles} />
                    ) : (
                      <Text type="secondary">Preview not available</Text>
                    )}
                  </div>

                  {video.external_url && (
                    <div>
                      <Text strong>External URL</Text>
                      <br />
                      <a href={video.external_url} target="_blank" rel="noopener noreferrer">
                        <GlobalOutlined /> Open Link
                      </a>
                    </div>
                  )}

                  <div>
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={handleTranscode}
                      loading={transcoding}
                      disabled={transcoding || !video.file_url || !ableTranscode}
                      style={{ width: '100%' }}
                    >
                      {transcoding ? 'Transcoding...' : 'Start Transcode'}
                    </Button>
                    {!video.file_url && (
                      <Text
                        type="secondary"
                        style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}
                      >
                        Transcode is only available for uploaded files URL
                      </Text>
                    )}
                    {video.file_url &&
                      video.status !== 'COMPLETED' &&
                      video.status !== 'completed' && (
                        <Text
                          type="warning"
                          style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}
                        >
                          Transcode is only available when video status is COMPLETED
                        </Text>
                      )}
                    <Button
                      type="default"
                      icon={<FireOutlined />}
                      onClick={() => {
                        setSelectedBurnInLangs([]);
                        setBurnInModalVisible(true);
                      }}
                      disabled={
                        !video ||
                        video.status !== 'COMPLETED' ||
                        subtitles.length === 0 ||
                        !video?.contents?.length
                      }
                      block
                      style={{ marginTop: 8 }}
                    >
                      Burn-in Subtitle
                    </Button>
                    {video && !video?.contents?.length && (
                      <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                        에피소드에 먼저 연결해주세요
                      </div>
                    )}
                  </div>

                  <div>
                    <Button
                      type="default"
                      onClick={handleRecover}
                      loading={recovering}
                      disabled={recovering || video.status?.toLowerCase() === 'recovering'}
                      style={{ width: '100%' }}
                    >
                      {video.status?.toLowerCase() === 'recovering'
                        ? 'Recovering...'
                        : 'Recover Original File'}
                    </Button>
                    {video.status?.toLowerCase() === 'recovering' && (
                      <Text
                        type="warning"
                        style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}
                      >
                        <LoadingOutlined style={{ marginRight: '4px' }} />
                        Recovery is in progress.
                      </Text>
                    )}
                  </div>
                </Space>
              ) : (
                <div
                  style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    border: '1px dashed #d9d9d9',
                    color: '#666',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  <Text type="secondary">📁 Original video file is not available</Text>
                  <br />
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                    Transcoding completed videos are available for service.
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                    Cannot retranscode without original video file.
                  </Text>
                  <br />
                  <br />
                  <Button
                    type="default"
                    onClick={handleRecover}
                    loading={recovering}
                    disabled={recovering || video.status?.toLowerCase() === 'recovering'}
                  >
                    {video.status?.toLowerCase() === 'recovering'
                      ? 'Recovering...'
                      : 'Recover Original File'}
                  </Button>
                  {video.status?.toLowerCase() === 'recovering' && (
                    <>
                      <br />
                      <Text
                        type="warning"
                        style={{ fontSize: '12px', marginTop: '8px' }}
                      >
                        <LoadingOutlined style={{ marginRight: '4px' }} />
                        Recovery is in progress.
                      </Text>
                    </>
                  )}
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card
              title={
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>Subtitle Management</span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openSubtitleModal()}
                  >
                    Add Subtitle
                  </Button>
                </div>
              }
              style={{ minHeight: '500px', height: '100%' }}
            >
              {subtitles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <FileTextOutlined
                    style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}
                  />
                  <Title level={4} type="secondary">
                    No subtitles registered
                  </Title>
                  <Text type="secondary">Start by adding subtitles</Text>
                  <br />
                </div>
              ) : (
                <List
                  dataSource={subtitles}
                  renderItem={subtitle => {
                    const langInfo = getLanguageInfo(subtitle.lang);

                    return (
                      <List.Item
                        actions={[
                          <Button
                            key="delete"
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => {
                              Modal.confirm({
                                title: 'Delete Subtitle',
                                content: 'Are you sure you want to delete this subtitle?',
                                onOk: () => handleDeleteSubtitle(subtitle.id!),
                              });
                            }}
                          />,
                          (subtitle.file || subtitle.external_url) && (
                            <Button
                              key="download"
                              type="text"
                              icon={<DownloadOutlined />}
                              onClick={() => {
                                let downloadUrl = '';
                                if (subtitle.file) {
                                  downloadUrl = subtitle.file as string;
                                } else if (subtitle.external_url) {
                                  const CF_URL = 'https://d18jiv46mtzvqq.cloudfront.net';
                                  const externalUrl = subtitle.external_url as string;

                                  // Handle s3:// format
                                  if (externalUrl.startsWith('s3://')) {
                                    // Extract only path from s3://bucket-name/path format
                                    const s3Path = externalUrl.replace(/^s3:\/\/[^/]+\//, '/');
                                    downloadUrl = CF_URL + s3Path;
                                  } else {
                                    // Already an HTTP URL or relative path
                                    downloadUrl = externalUrl.startsWith('http')
                                      ? externalUrl
                                      : CF_URL + externalUrl;
                                  }
                                }

                                if (downloadUrl) {
                                  fetch(downloadUrl)
                                    .then(response => response.blob())
                                    .then(blob => {
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.style.display = 'none';
                                      a.href = url;
                                      const urlParts = downloadUrl.split('/');
                                      const fileNameWithQuery =
                                        urlParts[urlParts.length - 1] || 'subtitle.srt';
                                      const fileName = fileNameWithQuery.split('?')[0];
                                      a.download = fileName;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    })
                                    .catch(() => {
                                      window.open(downloadUrl, '_blank');
                                    });
                                }
                              }}
                            />
                          ),
                        ].filter(Boolean)}
                      >
                        <List.Item.Meta
                          title={
                            <div
                              style={{
                                minWidth: 0,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-all',
                                overflowWrap: 'break-word',
                              }}
                            >
                              <Text strong>
                                {langInfo.label + ' / (' + subtitle.lang.toUpperCase() + ')'}
                              </Text>
                              {subtitle.subtitle_url && (
                                <>
                                  {' '}
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      padding: '0 7px',
                                      fontSize: '12px',
                                      lineHeight: '20px',
                                      color: '#1890ff',
                                      backgroundColor: '#e6f7ff',
                                      border: '1px solid #91d5ff',
                                      borderRadius: '2px',
                                      wordBreak: 'break-all',
                                      overflowWrap: 'break-word',
                                      maxWidth: '100%',
                                      marginTop: '2px',
                                    }}
                                  >
                                    {
                                      (subtitle.subtitle_url ||
                                        subtitle.external_url ||
                                        '') as string
                                    }
                                  </span>
                                </>
                              )}
                            </div>
                          }
                          description={
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {subtitle.created &&
                                  `Created: ${new Date(subtitle.created).toLocaleString()}`}
                              </Text>
                            </div>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              )}
            </Card>

            <Card title="Burn-in Variants" size="small" style={{ marginTop: 16 }}>
              {burnInJobs.length === 0 ? (
                <Empty description="No burn-in variants" />
              ) : (
                <List
                  size="small"
                  dataSource={burnInJobs}
                  renderItem={(job: BurnInJob) => (
                    <List.Item
                      actions={[
                        job.status === 'completed' && job.result_content_id ? (
                          <a href={`/episode/edit/${job.result_content_id}`} key="result">View Episode</a>
                        ) : null,
                      ].filter(Boolean)}
                    >
                      <Space>
                        <span style={{ fontWeight: 'bold' }}>{job.lang.toUpperCase()}</span>
                        <BurnInStatusTag status={job.status} />
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CameraOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  <span>Thumbnail Picker (Default)</span>
                </div>
              }
            >
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={10}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Video Preview
                    </Text>
                    {hasPlayableVideoUrl(video) ? (
                      <div
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: '8px',
                          overflow: 'hidden',
                        }}
                      >
                        <ReactPlayer
                          ref={playerRef}
                          url={getPlayableVideoUrl(video)}
                          controls
                          width="100%"
                          height="auto"
                          config={{
                            file: {
                              attributes: {
                                controlsList: 'nodownload',
                                crossOrigin: 'anonymous',
                              },
                            },
                          }}
                          onReady={() => {
                            if (playerRef.current) {
                              const internalPlayer = playerRef.current.getInternalPlayer();
                              if (internalPlayer instanceof HTMLVideoElement) {
                                if (!internalPlayer.crossOrigin) {
                                  internalPlayer.crossOrigin = 'anonymous';
                                  const currentTime = internalPlayer.currentTime;
                                  internalPlayer.load();
                                  internalPlayer.oncanplay = () => {
                                    if (currentTime > 0) {
                                      internalPlayer.currentTime = currentTime;
                                    }
                                  };
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          border: '1px dashed #d9d9d9',
                          borderRadius: '8px',
                          padding: '40px',
                          textAlign: 'center',
                        }}
                      >
                        <Text type="secondary">No video available</Text>
                      </div>
                    )}
                  </div>
                </Col>

                <Col
                  xs={24}
                  md={4}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Button
                    type="primary"
                    onClick={handleTakeScreenshot}
                    disabled={!hasPlayableVideoUrl(video)}
                    style={{
                      height: 'auto',
                      padding: '16px 8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <CameraOutlined style={{ fontSize: '24px' }} />
                    <span style={{ fontSize: '12px', lineHeight: '1.2', textAlign: 'center' }}>
                      Take
                      <br />
                      Screenshot
                    </span>
                  </Button>
                </Col>

                <Col xs={24} md={10}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Default Thumbnail Preview (1280x720)
                    </Text>
                    <div
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '16px',
                        minHeight: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      {thumbnailImage ? (
                        <img
                          src={thumbnailImage}
                          alt="Default Thumbnail"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: '4px',
                          }}
                        />
                      ) : (
                        <Text type="secondary">No thumbnail captured yet</Text>
                      )}
                    </div>
                    {thumbnailImage && (
                      <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={handleSaveDefaultThumbnail}
                          loading={saving}
                          size="large"
                        >
                          Save Default Thumbnail
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Thumbnail Language Override Section */}
        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24} lg={16}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  <span>Thumbnail Language override</span>
                </div>
              }
            >
              <Row gutter={[16, 16]}>
                {THUMBNAIL_LANGUAGES.map(lang => {
                  const existingThumbnail = languageThumbnails.get(lang.value);
                  const uploadedImage = uploadedThumbnails.get(lang.value);
                  const displayImage = uploadedImage || existingThumbnail?.url || null;

                  return (
                    <Col xs={24} sm={12} md={6} key={lang.value}>
                      <div
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: '8px',
                          padding: '16px',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 'bold' }}>
                          {lang.label}
                        </div>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={file => {
                            handleThumbnailUpload(lang.value, file);
                            return false; // Prevent default upload
                          }}
                        >
                          <div
                            style={{
                              border: displayImage ? '2px solid #1890ff' : '2px dashed #d9d9d9',
                              borderRadius: '8px',
                              minHeight: '140px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: displayImage ? '#f6f8fa' : '#fafafa',
                              marginBottom: '12px',
                              position: 'relative',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              overflow: 'hidden',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = '#1890ff';
                              e.currentTarget.style.backgroundColor = displayImage
                                ? '#e6f7ff'
                                : '#f0f0f0';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow =
                                '0 4px 12px rgba(24, 144, 255, 0.15)';
                              const hint = e.currentTarget.querySelector(
                                '.upload-hint'
                              ) as HTMLElement;
                              if (hint) {
                                hint.style.opacity = '1';
                              }
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = displayImage
                                ? '#1890ff'
                                : '#d9d9d9';
                              e.currentTarget.style.backgroundColor = displayImage
                                ? '#f6f8fa'
                                : '#fafafa';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              const hint = e.currentTarget.querySelector(
                                '.upload-hint'
                              ) as HTMLElement;
                              if (hint) {
                                hint.style.opacity = '0';
                              }
                            }}
                          >
                            {displayImage ? (
                              <>
                                <img
                                  src={displayImage}
                                  alt={`${lang.label} thumbnail`}
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '120px',
                                    borderRadius: '4px',
                                    objectFit: 'contain',
                                  }}
                                />
                                <div
                                  className="upload-hint"
                                  style={{
                                    position: 'absolute',
                                    top: '6px',
                                    right: '6px',
                                    backgroundColor: 'rgba(24, 144, 255, 0.95)',
                                    color: 'white',
                                    borderRadius: '6px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  Click to replace
                                </div>
                              </>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '20px',
                                }}
                              >
                                <div
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <UploadOutlined
                                    style={{
                                      fontSize: '24px',
                                      color: '#8c8c8c',
                                    }}
                                  />
                                </div>
                                <div
                                  style={{
                                    fontSize: '13px',
                                    color: '#595959',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                  }}
                                >
                                  Click to upload
                                  <br />
                                  <span
                                    style={{
                                      fontSize: '11px',
                                      color: '#8c8c8c',
                                      fontWeight: 400,
                                    }}
                                  >
                                    Image file
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </Upload>
                        {displayImage && existingThumbnail && (
                          <div style={{ marginTop: '12px' }}>
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                Modal.confirm({
                                  title: 'Delete Thumbnail',
                                  content: `Are you sure you want to delete the ${lang.label} thumbnail?`,
                                  onOk: () => handleDeleteLanguageThumbnail(lang.value),
                                });
                              }}
                              block
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={handleSaveAllLanguageThumbnails}
                  loading={saving}
                  disabled={Array.from(uploadedThumbnails.values()).length === 0}
                >
                  Save All Language Thumbnails
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title="Thumbnail Distribution Rule"
              style={{
                backgroundColor: '#fffbe6',
                border: '1px solid #ffe58f',
              }}
            >
              <div style={{ padding: '8px 0' }}>
                {THUMBNAIL_DISTRIBUTION_RULES.map((rule, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 0',
                      borderBottom:
                        index < THUMBNAIL_DISTRIBUTION_RULES.length - 1
                          ? '1px solid #ffe58f'
                          : 'none',
                    }}
                  >
                    <Text strong>{rule.language}</Text>
                    <Text> : {rule.service}</Text>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <GlobalOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  <span>Source</span>
                  <Tag color="blue" style={{ marginLeft: '12px' }}>
                    {video.stream_manifests ? video.stream_manifests.length : 0} Manifests
                  </Tag>
                </div>
              }
            >
              {video.stream_manifests && video.stream_manifests.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {video.stream_manifests.map((manifest, index) => (
                    <Col xs={24} sm={12} lg={8} key={index}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: '8px',
                          border: `2px solid ${
                            getStatusDisplay(manifest.status).color === 'green'
                              ? '#52c41a'
                              : getStatusDisplay(manifest.status).color === 'orange'
                                ? '#faad14'
                                : getStatusDisplay(manifest.status).color === 'blue'
                                  ? '#1890ff'
                                  : getStatusDisplay(manifest.status).color === 'red'
                                    ? '#ff4d4f'
                                    : '#d9d9d9'
                          }`,
                        }}
                        bodyStyle={{ padding: '16px' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                            <Text strong style={{ fontSize: '14px' }}>
                              {manifest.type?.toUpperCase() || 'Stream'}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {manifest.preset}
                            </Text>
                          </div>

                          <div style={{ textAlign: 'center' }}>
                            <Tag
                              color={getStatusDisplay(manifest.status).color}
                              style={{ fontSize: '11px' }}
                            >
                              {getStatusDisplay(manifest.status).label}
                            </Tag>
                          </div>

                          {(() => {
                            const manifestAny = manifest as any;
                            const hlsUrl =
                              manifestAny.url ||
                              manifestAny.hls_url ||
                              manifestAny.stream_url ||
                              manifestAny.manifest_url ||
                              manifestAny.file_url ||
                              manifestAny.playback_url;
                            const resolvedHlsUrl =
                              hlsUrl && !hlsUrl.startsWith('http')
                                ? `https://hls-mh.ondemandkorea.com/${hlsUrl}`
                                : hlsUrl;

                            if (
                              (manifest.status === 'completed' ||
                                manifest.status === 'COMPLETED') &&
                              hlsUrl
                            ) {
                              return (
                                <div>
                                  <Text strong style={{ fontSize: '12px' }}>
                                    HLS Preview:
                                  </Text>
                                  <div
                                    style={{
                                      marginTop: '8px',
                                      border: '1px solid #f0f0f0',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    <Preview
                                      videoUrl={manifest.stream_url_full || manifest.stream_url}
                                      subtitles={subtitles}
                                    />
                                  </div>

                                  <div style={{ marginTop: '8px' }}>
                                    <Text
                                      style={{
                                        fontSize: '10px',
                                        color: '#666',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      {resolvedHlsUrl}
                                    </Text>
                                  </div>

                                  <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                    <Space size="small">
                                      <Button
                                        size="small"
                                        type="link"
                                        icon={<GlobalOutlined />}
                                        onClick={() => {
                                          navigator.clipboard.writeText(resolvedHlsUrl);
                                          message.success('URL copied to clipboard');
                                        }}
                                        style={{ fontSize: '11px', padding: '0' }}
                                      >
                                        Copy URL
                                      </Button>
                                      <Button
                                        size="small"
                                        type="link"
                                        onClick={() => window.open(resolvedHlsUrl, '_blank')}
                                        style={{ fontSize: '11px', padding: '0' }}
                                      >
                                        Open
                                      </Button>
                                    </Space>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {[
                            'IN_PROGRESS',
                            'CREATING',
                            'START',
                            'in_progress',
                            'processing',
                            'TRANSCODING',
                            'creating',
                            'uploading',
                          ].includes(manifest.status) && (
                            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                              <Spin size="small" />
                              <br />
                              <Text type="secondary" style={{ fontSize: '11px', marginTop: '8px' }}>
                                {getStatusDisplay(manifest.status).label}
                              </Text>
                            </div>
                          )}

                          {(manifest.status === 'failed' || manifest.status === 'FAILED') && (
                            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                              <Text type="danger" style={{ fontSize: '11px' }}>
                                Processing Failed
                              </Text>
                            </div>
                          )}
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <GlobalOutlined
                    style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}
                  />
                  <Title level={4} type="secondary">
                    No stream manifests available
                  </Title>
                  <Text type="secondary">No HLS streams have been generated yet</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Contents List */}
        {video.contents && video.contents.length > 0 && (
          <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
            <Col xs={24}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <span>Linked Contents</span>
                    <Tag color="blue" style={{ marginLeft: '12px' }}>
                      {video.contents.length} Contents
                    </Tag>
                  </div>
                }
              >
                <List
                  dataSource={video.contents}
                  renderItem={content => (
                    <List.Item
                      style={{
                        cursor: 'pointer',
                        padding: '12px 16px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        transition: 'all 0.3s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                        e.currentTarget.style.borderColor = '#1890ff';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#f0f0f0';
                      }}
                      onClick={() => {
                        if (content.content_type === 'movie') {
                          window.open(`/movie/edit/${content.id}`, '_blank');
                        } else {
                          window.open(
                            `/episode/edit/${content.id}?content_type=${content.content_type}`,
                            '_blank'
                          );
                        }
                      }}
                    >
                      <List.Item.Meta
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text strong>ID: {content.id}</Text>
                            <Tag color={content.content_type === 'movie' ? 'purple' : 'blue'}>
                              {content.content_type === 'movie' ? 'Movie' : 'Episode'}
                            </Tag>
                          </div>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Slug: {content.slug}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Dev Mode: Video File Verification */}
        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Tag color="orange" style={{ marginRight: '8px' }}>
                    Dev Mode
                  </Tag>
                  <span>Video File Verification</span>
                </div>
              }
              style={{
                border: '1px solid #ffa940',
                backgroundColor: '#fff7e6',
              }}
            >
              <div style={{ padding: '4px 0' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                  Checks if all VideoStreamManifests are completed and submits a file verification batch job.
                </Text>
                <Button
                  type="primary"
                  danger
                  onClick={handleVerifyVideoFiles}
                  loading={verifyingVideo}
                  disabled={verifyingVideo}
                  size="large"
                >
                  {verifyingVideo ? 'Verifying...' : 'Submit Video File Verification Batch Job'}
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {video.video_histories && (
          <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
            <Col xs={24}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <span>Video History</span>
                    <Tag color="blue" style={{ marginLeft: '12px' }}>
                      {video.video_histories.length} Actions
                    </Tag>
                  </div>
                }
                style={{
                  maxHeight: '600px',
                  overflowY: 'auto',
                  scrollBehavior: 'smooth',
                }}
                bodyStyle={{
                  paddingRight: '12px',
                }}
              >
                {video.video_histories.length > 0 ? (
                  <Timeline>
                    {video.video_histories
                      .sort((a, b) => {
                        if (a.created && b.created) {
                          return new Date(b.created).getTime() - new Date(a.created).getTime();
                        }
                        return b.id - a.id;
                      })
                      .map((history, _index) => (
                        <Timeline.Item
                          key={history.id}
                          color={
                            history.status === 'COMPLETED' || history.status === 'completed'
                              ? 'green'
                              : history.status === 'FAILED' || history.status === 'failed'
                                ? 'red'
                                : history.status === 'IN_PROGRESS' ||
                                    history.status === 'in_progress'
                                  ? 'blue'
                                  : 'gray'
                          }
                        >
                          <div
                            style={{
                              background: '#fafafa',
                              padding: '12px',
                              borderRadius: '6px',
                              border: '1px solid #e8e8e8',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '8px',
                              }}
                            >
                              <Text strong style={{ fontSize: '14px' }}>
                                {history.action}
                              </Text>
                              <Tag
                                color={getStatusDisplay(history.status).color}
                                style={{ fontSize: '11px' }}
                              >
                                {getStatusDisplay(history.status).label}
                              </Tag>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                fontSize: '12px',
                                color: '#666',
                              }}
                            >
                              <span>
                                <UserOutlined style={{ marginRight: '4px' }} />
                                {history.user || 'Unknown'}
                              </span>
                              <span>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {history.created
                                  ? new Date(history.created).toLocaleString()
                                  : `ID: ${history.id}`}
                              </span>
                            </div>
                          </div>
                        </Timeline.Item>
                      ))}
                  </Timeline>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <HistoryOutlined
                      style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}
                    />
                    <Title level={4} type="secondary">
                      No video history available
                    </Title>
                    <Text type="secondary">No video actions have been performed yet</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* User Action History */}
        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <HistoryOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  <span>Action History</span>
                  <Tag color="blue" style={{ marginLeft: '12px' }}>
                    {userActionHistories.length} Actions
                  </Tag>
                </div>
              }
              style={{
                maxHeight: '600px',
                overflowY: 'auto',
                scrollBehavior: 'smooth',
              }}
              bodyStyle={{
                paddingRight: '12px',
              }}
            >
              {userActionHistories.length > 0 ? (
                <Timeline>
                  {userActionHistories
                    .sort((a, b) => {
                      if (a.created_at && b.created_at) {
                        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                      }
                      return b.id - a.id;
                    })
                    .map(history => (
                      <Timeline.Item
                        key={history.id}
                        color={
                          history.status_code &&
                          history.status_code >= 200 &&
                          history.status_code < 300
                            ? 'green'
                            : history.status_code && history.status_code >= 400
                              ? 'red'
                              : 'gray'
                        }
                      >
                        <div
                          style={{
                            background: '#fafafa',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '1px solid #e8e8e8',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '8px',
                            }}
                          >
                            <Text strong style={{ fontSize: '14px' }}>
                              {history.action}
                            </Text>
                            {history.status_code && (
                              <Tag
                                color={
                                  history.status_code >= 200 && history.status_code < 300
                                    ? 'green'
                                    : history.status_code >= 400
                                      ? 'red'
                                      : 'default'
                                }
                                style={{ fontSize: '11px' }}
                              >
                                {history.status_code}
                              </Tag>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              fontSize: '12px',
                              color: '#666',
                              marginBottom: '8px',
                            }}
                          >
                            <span>
                              <UserOutlined style={{ marginRight: '4px' }} />
                              {history.user_username || 'Unknown'}
                            </span>
                            {history.user_email && (
                              <span style={{ fontSize: '11px', color: '#999' }}>
                                ({history.user_email})
                              </span>
                            )}
                            <span>
                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                              {history.created_at
                                ? new Date(history.created_at).toLocaleString()
                                : `ID: ${history.id}`}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                            <Text type="secondary">
                              Object: {history.object_type_name} #{history.object_id}
                            </Text>
                            {history.object_title && (
                              <Text type="secondary" style={{ marginLeft: '8px' }}>
                                - {history.object_title}
                              </Text>
                            )}
                          </div>
                          {history.changed_fields && history.changed_fields.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <Text
                                type="secondary"
                                style={{ fontSize: '11px', fontWeight: 'bold', display: 'block' }}
                              >
                                Changed Fields:
                              </Text>
                              <div style={{ marginTop: '4px' }}>
                                {history.changed_fields.map((field, idx) => (
                                  <Tag key={idx} style={{ fontSize: '10px', marginBottom: '4px' }}>
                                    {field}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                </Timeline>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <HistoryOutlined
                    style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}
                  />
                  <Title level={4} type="secondary">
                    No action history available
                  </Title>
                  <Text type="secondary">No user actions have been performed yet</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Modal
          title={editingSubtitle ? 'Edit Subtitle' : 'Add Subtitle'}
          open={subtitleModalVisible}
          onCancel={() => setSubtitleModalVisible(false)}
          footer={null}
          width={600}
        >
          <Form form={subtitleForm} layout="vertical" onFinish={handleSaveSubtitle}>
            <Form.Item
              label="Language"
              name="lang"
              rules={[{ required: true, message: 'Please select a language' }]}
            >
              <Select placeholder="Select Language">
                {subtitleLanguages.length > 0
                  ? subtitleLanguages
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map(lang => (
                        <Select.Option key={lang.id} value={lang.rfc_code.toLowerCase()}>
                          {lang.name + ' (' + lang.rfc_code.toLowerCase() + ')'}
                        </Select.Option>
                      ))
                  : LANGUAGE_OPTIONS.map(lang => (
                      <Select.Option key={lang.value} value={lang.value}>
                        {lang.label + ' (' + lang.value.toLowerCase() + ')'}
                      </Select.Option>
                    ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Subtitle File"
              name="file"
              valuePropName="fileList"
              getValueFromEvent={e => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
              rules={[
                {
                  required: !editingSubtitle,
                  message: 'Please select a file',
                },
                {
                  validator: (_, value) => {
                    if (uploadMethod === 'file' && (!value || value.length === 0)) {
                      return Promise.reject(new Error('Please select a file'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Upload.Dragger
                beforeUpload={() => false}
                accept=".srt,.vtt,.ass,.ssa"
                maxCount={1}
                onChange={info => {
                  subtitleForm.setFieldValue('file', info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">
                  Click or drag subtitle file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for SRT, VTT, ASS, SSA files. Single file upload only.
                </p>
              </Upload.Dragger>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setSubtitleModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                  Save
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Burn-in Subtitle"
          open={burnInModalVisible}
          onOk={handleBurnIn}
          onCancel={() => setBurnInModalVisible(false)}
          confirmLoading={burnInLoading}
          okText="Start Burn-in"
          okButtonProps={{ disabled: selectedBurnInLangs.length === 0 }}
        >
          <p>Select subtitle languages to burn in:</p>
          <Checkbox.Group
            value={selectedBurnInLangs}
            onChange={(values) => setSelectedBurnInLangs(values as number[])}
          >
            <Space direction="vertical">
              {subtitles.map((sub) => (
                <Checkbox key={sub.id} value={sub.id}>
                  <span style={{ wordBreak: 'break-all' }}>
                    {sub.lang.toUpperCase()} — {sub.subtitle_url || sub.external_url || 'No file'}
                  </span>
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Modal>
      </div>
    </Component.BasePageComponent>
  );
};

export default AssetsEdit;
